import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, Heart, ShoppingBag, Truck, ShieldCheck, RotateCcw, Minus, Plus, ChevronRight, Check, CreditCard, Camera, X, Loader2 } from "lucide-react";
import api, { gbp } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function ReviewForm({ productId, onDone }) {
  const { user } = useAuth();
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  if (!user)
    return (
      <div className="border border-border bg-card p-5 text-sm">
        <Link to="/login" className="font-semibold text-accent">Sign in</Link> to leave a review and share your photos.
      </div>
    );

  const onPick = (e) => {
    const picked = Array.from(e.target.files || []).slice(0, 4);
    setFiles(picked);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) { toast.error("Please write a few words about the product."); return; }
    setBusy(true);
    try {
      const photos = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/reviews/photo", fd);
        photos.push(data.url);
      }
      await api.post("/reviews", { product_id: productId, rating, body, photos });
      toast.success("Thanks — your review has been posted!");
      setBody(""); setFiles([]); setRating(5);
      onDone();
    } catch (err) {
      toast.error("Could not post your review. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid="review-form" className="border border-border bg-card p-5">
      <h3 className="mb-4 font-display text-lg font-semibold uppercase">Write a Review</h3>
      <div className="mb-4 flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button type="button" key={n} data-testid={`review-star-${n}`} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => setRating(n)}>
            <Star className={`h-7 w-7 transition-colors ${n <= (hover || rating) ? "fill-accent text-accent" : "text-muted-foreground"}`} />
          </button>
        ))}
      </div>
      <textarea data-testid="review-body" value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="How did it perform on the bank?" className="w-full border border-border bg-secondary/60 px-3 py-2.5 text-sm outline-none focus:border-accent transition-colors" />
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <label className="flex cursor-pointer items-center gap-2 border border-border px-4 py-2.5 text-xs font-bold uppercase tracking-widest hover:border-accent transition-colors">
          <Camera className="h-4 w-4" /> Add Photos
          <input type="file" accept="image/*" multiple onChange={onPick} data-testid="review-photo-input" className="hidden" />
        </label>
        {files.length > 0 && <span className="text-xs text-muted-foreground">{files.length} photo(s) selected</span>}
      </div>
      <button type="submit" disabled={busy} data-testid="review-submit" className="mt-4 flex items-center gap-2 bg-accent px-6 py-3 text-xs font-bold uppercase tracking-widest text-accent-foreground hover:brightness-110 transition-[filter] disabled:opacity-50">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} {busy ? "Posting…" : "Submit Review"}
      </button>
    </form>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [qty, setQty] = useState(1);
  const [zoom, setZoom] = useState({ show: false, x: 50, y: 50 });
  const imgRef = useRef(null);
  const { addItem } = useCart();
  const { has, toggle } = useWishlist();

  const fetchProduct = useCallback(() => {
    api.get(`/products/${slug}`).then((r) => setData(r.data)).catch(() => setData("error"));
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setQty(1);
    fetchProduct();
  }, [slug, fetchProduct]);

  if (data === "error") return <div className="container-furn py-24 text-center"><h1 className="font-display text-3xl">Product not found</h1><Link to="/shop" className="mt-4 inline-block text-accent">Back to shop</Link></div>;
  if (!data) return <div className="container-furn py-24"><div className="h-96 animate-pulse bg-secondary" /></div>;

  const p = data.product;
  const onSale = !!p.sale_price;
  const eff = onSale ? p.sale_price : p.price;
  const wished = has(p.id);

  const onMove = (e) => {
    const r = imgRef.current.getBoundingClientRect();
    setZoom({ show: true, x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
  };

  return (
    <div className="container-furn py-8">
      <nav className="mb-6 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
        <Link to="/" className="hover:text-accent">Home</Link><ChevronRight className="h-3 w-3" />
        <Link to={`/shop?discipline=${encodeURIComponent(p.discipline)}`} className="hover:text-accent">{p.discipline}</Link><ChevronRight className="h-3 w-3" />
        <Link to={`/shop?discipline=${encodeURIComponent(p.discipline)}&category=${encodeURIComponent(p.category)}`} className="hover:text-accent">{p.category}</Link><ChevronRight className="h-3 w-3" />
        <span className="text-foreground line-clamp-1">{p.name}</span>
      </nav>

      <div className="grid gap-10 lg:grid-cols-2">
        {/* Gallery */}
        <div className="lg:sticky lg:top-32 lg:self-start">
          <div ref={imgRef} onMouseEnter={() => setZoom((z) => ({ ...z, show: true }))} onMouseLeave={() => setZoom((z) => ({ ...z, show: false }))} onMouseMove={onMove} className="relative aspect-square overflow-hidden border border-border bg-secondary cursor-zoom-in">
            <img src={p.images[0]} alt={p.name} className="h-full w-full object-cover" style={zoom.show ? { transform: "scale(1.8)", transformOrigin: `${zoom.x}% ${zoom.y}%` } : {}} />
            <div className="absolute left-3 top-3 flex flex-col gap-1">
              {p.is_new && <span className="bg-accent px-2 py-1 text-[10px] font-bold uppercase text-accent-foreground">New</span>}
              {onSale && <span className="bg-destructive px-2 py-1 text-[10px] font-bold uppercase text-white">Save {gbp(p.price - p.sale_price)}</span>}
            </div>
          </div>
          <div className="mt-3 grid grid-cols-4 gap-3">
            {[p.images[0], p.images[0], p.images[0], p.images[0]].map((src, i) => (
              <div key={i} className={`aspect-square overflow-hidden border ${i === 0 ? "border-accent" : "border-border"} bg-secondary`}><img src={src} alt="" className="h-full w-full object-cover opacity-90" /></div>
            ))}
          </div>
        </div>

        {/* Info */}
        <div>
          <span className="overline">{p.brand}</span>
          <h1 className="mt-2 font-display text-3xl font-semibold uppercase leading-tight md:text-4xl">{p.name}</h1>
          <div className="mt-3 flex items-center gap-3">
            <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`h-4 w-4 ${i < Math.round(p.rating) ? "fill-accent text-accent" : "text-muted-foreground"}`} />)}</div>
            <span className="text-sm text-muted-foreground">{p.rating} · {p.review_count} reviews</span>
            <span className="text-xs text-muted-foreground">SKU: {p.sku}</span>
          </div>

          <div className="mt-6 flex items-baseline gap-3">
            <span className="font-display text-4xl font-bold">{gbp(eff)}</span>
            {onSale && <span className="text-xl text-muted-foreground line-through">{gbp(p.price)}</span>}
          </div>

          <div className="mt-3 flex items-center gap-2 text-sm">
            {p.stock > 0 ? <span className="flex items-center gap-1.5 text-accent"><Check className="h-4 w-4" /> In stock ({p.stock} available)</span> : <span className="text-destructive">Out of stock</span>}
          </div>

          <p className="mt-5 text-base leading-relaxed text-foreground/80">{p.description}</p>

          {/* Finance calculator */}
          <div className="mt-5 flex items-center gap-3 border border-border bg-card p-4 text-sm">
            <CreditCard className="h-5 w-5 text-accent" />
            <span>Or 3 interest-free payments of <b>{gbp(eff / 3)}</b> with Klarna & Clearpay.</span>
          </div>

          {/* Add to cart */}
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <div className="flex items-center border border-border">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="grid h-14 w-12 place-items-center hover:text-accent"><Minus className="h-4 w-4" /></button>
              <span className="w-12 text-center text-lg font-semibold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="grid h-14 w-12 place-items-center hover:text-accent"><Plus className="h-4 w-4" /></button>
            </div>
            <button data-testid="pdp-add-to-cart" disabled={p.stock === 0} onClick={() => addItem(p, qty)} className="flex flex-1 items-center justify-center gap-2 bg-accent py-4 text-xs font-bold uppercase tracking-widest text-accent-foreground hover:brightness-110 transition-[filter] disabled:opacity-40">
              <ShoppingBag className="h-4 w-4" /> Add to Basket
            </button>
            <button data-testid="pdp-wishlist" onClick={() => { toggle(p); toast.success(wished ? "Removed from wishlist" : "Added to wishlist"); }} className="grid h-14 w-14 place-items-center border border-border hover:border-accent transition-colors">
              <Heart className={`h-5 w-5 ${wished ? "fill-accent text-accent" : ""}`} />
            </button>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-3 border-t border-border pt-6 text-sm sm:grid-cols-3">
            {[[Truck, "Free UK delivery over £50"], [RotateCcw, "30-day easy returns"], [ShieldCheck, "Secure checkout"]].map(([Icon, t]) => (
              <div key={t} className="flex items-center gap-2 text-muted-foreground"><Icon className="h-4 w-4 text-accent" /> {t}</div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-16">
        <Tabs defaultValue="specs">
          <TabsList className="w-full justify-start overflow-x-auto no-scrollbar">
            <TabsTrigger value="specs" data-testid="tab-specs">Specifications</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews">Reviews ({p.review_count})</TabsTrigger>
            <TabsTrigger value="delivery">Delivery & Returns</TabsTrigger>
            <TabsTrigger value="qa">Q&A</TabsTrigger>
          </TabsList>
          <TabsContent value="specs" className="pt-6">
            <div className="max-w-2xl divide-y divide-border border border-border">
              {Object.entries(p.specs).map(([k, v]) => (
                <div key={k} className="flex justify-between px-4 py-3 text-sm"><span className="text-muted-foreground">{k}</span><span className="font-medium">{v}</span></div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="reviews" className="pt-6">
            <div className="max-w-2xl space-y-6">
              <ReviewForm productId={p.id} onDone={fetchProduct} />
              <div className="space-y-4">
                {(data.user_reviews || []).map((r) => (
                  <div key={r.id} data-testid="user-review" className="border border-accent/30 bg-card p-5">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">{r.author}</span>
                      <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, j) => <Star key={j} className={`h-3.5 w-3.5 ${j < r.rating ? "fill-accent text-accent" : "text-muted-foreground"}`} />)}</div>
                    </div>
                    {r.verified && <span className="mt-1 inline-block text-xs text-accent">✓ Verified purchase</span>}
                    <p className="mt-2 text-sm text-foreground/80">{r.body}</p>
                    {r.photos && r.photos.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {r.photos.map((ph, k) => (
                          <a key={k} href={`${BACKEND}${ph}`} target="_blank" rel="noreferrer" className="h-20 w-20 overflow-hidden border border-border bg-secondary">
                            <img src={`${BACKEND}${ph}`} alt="Customer review" className="h-full w-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {p.reviews.map((r, i) => (
                  <div key={i} className="border border-border bg-card p-5">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">{r.author}</span>
                      <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, j) => <Star key={j} className={`h-3.5 w-3.5 ${j < r.rating ? "fill-accent text-accent" : "text-muted-foreground"}`} />)}</div>
                    </div>
                    {r.verified && <span className="mt-1 inline-block text-xs text-accent">✓ Verified purchase</span>}
                    <p className="mt-2 text-sm text-foreground/80">{r.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
          <TabsContent value="delivery" className="pt-6">
            <div className="max-w-2xl space-y-3 text-sm text-foreground/80">
              <p><b className="text-foreground">Standard Delivery</b> — Free on orders over £50 (otherwise £3.99). Delivered within 2–4 working days.</p>
              <p><b className="text-foreground">Next Day Delivery</b> — £6.99, order before 2pm Mon–Fri.</p>
              <p><b className="text-foreground">Click & Collect</b> — Free from our South Woodham Ferrers store.</p>
              <p><b className="text-foreground">Returns</b> — 30-day hassle-free returns via our returns portal.</p>
            </div>
          </TabsContent>
          <TabsContent value="qa" className="pt-6">
            <div className="max-w-2xl space-y-4 text-sm">
              <div className="border border-border bg-card p-4"><p className="font-semibold">Q: Is this suitable for beginners?</p><p className="mt-1 text-foreground/80">A: Absolutely — it's a great all-round choice and our team are happy to advise on setup.</p></div>
              <div className="border border-border bg-card p-4"><p className="font-semibold">Q: Do you price match?</p><p className="mt-1 text-foreground/80">A: Yes, get in touch at sales@furnangling.co.uk and we'll do our best.</p></div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Frequently bought together */}
      {data.related.length > 0 && (
        <div className="mt-16">
          <h2 className="mb-6 font-display text-2xl font-semibold uppercase">You may also like</h2>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {data.related.map((rp, i) => <ProductCard key={rp.id} product={rp} index={i} />)}
          </div>
        </div>
      )}
    </div>
  );
}
